import Cookies from 'universal-cookie'
import { v4 as uuidv4 } from 'uuid'
import { DataCapture } from './DataCapture'
import { isValidPhoneNumber } from 'react-phone-number-input'

export default class Participant {
  sessionId: string
  email: string | null
  firstName: string | null
  lastName: string | null
  phoneNumber: string | null
  acceptedTerms: Date | null

  constructor() {
    this.sessionId = uuidv4()
    this.email = null
    this.firstName = null
    this.lastName = null
    this.phoneNumber = null
    this.acceptedTerms = null
  }

  static resolvePersistenceKey(presetCode: string): string {
    return `preset_${presetCode}_participant`
  }

  static findOrCreateNew(presetCode: string): Participant {
    let participant = new Participant()
    participant.updateFromCookie(presetCode)
    participant.updateFromSearchParams()
    participant.save(presetCode)

    return participant
  }

  updateFromCookie(presetCode: string) {
    let cookies = new Cookies()
    let key = Participant.resolvePersistenceKey(presetCode)
    let cookie = cookies.get(key)
    if (!cookie) return

    for (const [key, value] of Object.entries(cookie)) {
      if (!(key in this)) continue
      if (value == null || value === "null") continue
      (this as any)[key] = value
    }
  }

  save(presetCode: string) {
    let cookies = new Cookies()

    cookies.set(
      Participant.resolvePersistenceKey(presetCode),
      this, {
      maxAge: 600 // Expire in 15 min
    })
  }

  toSearchParams(): URLSearchParams {
    let params = new URLSearchParams()
    params.set("sessionId", this.sessionId)

    if (this.email) {
      params.set("email", this.email)
    }
    if (this.firstName) {
      params.set("firstName", this.firstName)
    }
    if (this.lastName) {
      params.set("lastName", this.lastName)
    }
    if (this.phoneNumber) {
      params.set("phoneNumber", this.phoneNumber)
    }
    return params
  }

  updateFromSearchParams() {
    var params = new URLSearchParams(window.location.search)

    let sessionId = params.get("sessionId")
    let email = params.get("email")
    let firstName = params.get("firstName")
    let lastName = params.get("lastName")
    let phoneNumber = params.get("phoneNumber")

    if (sessionId && sessionId.length >= 16) {
      this.sessionId = sessionId
      params.delete("sessionId")
    }

    if (email && Participant.isValidEmail(email)) {
      this.email = email
      params.delete("email")
    }

    if (firstName && Participant.isValidName(firstName)) {
      this.firstName = firstName
      params.delete("firstName")
    }

    if (lastName && Participant.isValidName(lastName)) {
      this.lastName = lastName

      // Sanitize the last name field for Nemours
      if (lastName.includes("?")) {
        this.lastName = lastName.split("?")[0]
      }

      params.delete("lastName")
    }

    if (phoneNumber && isValidPhoneNumber(phoneNumber)) {
      this.phoneNumber = phoneNumber
      params.delete("phoneNumber")
    }

    // Replace search params with a list that omits the ones that have been handled
    let newUrl = window.location.pathname
    if (params.toString().length > 0) {
      newUrl += "?" + params.toString()
    }
    window.history.replaceState(null, document.title, newUrl)
  }

  hasExpectedData(dataCapture: DataCapture): boolean {
    if ((dataCapture.emailOn && (this.email === null || !Participant.isValidEmail(this.email))) ||
      (dataCapture.firstNameOn && (this.firstName === null || !Participant.isValidName(this.firstName))) ||
      (dataCapture.lastNameOn && (this.lastName === null || !Participant.isValidName(this.lastName))) ||
      (dataCapture.phoneNumberOn && (this.phoneNumber === null || !isValidPhoneNumber(this.phoneNumber)))
    )
      return false

    const anyFieldOn = dataCapture.emailOn || dataCapture.firstNameOn || dataCapture.lastNameOn || dataCapture.phoneNumberOn
    if (anyFieldOn && !this.acceptedTerms) return false

    return true
  }

  static isValidEmail(email: string): boolean {
    const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email)
  }

  static isValidName(name: string) {
    return name.length > 0
  }
}
